<script setup lang="ts">
const technologies = useTechnologies()
const projects = useProjects()

const route = useRoute()
const lp = useLocalePath()
const {
  public: {baseUrl},
} = useRuntimeConfig()

const slug = route.params.slug as string

const {pending, data: rawTech} = await useAsyncData(
  `technologies_${slug}`,
  () => technologies.fetchBySlug(route.params.slug as string),
  {watch: [route]}
)

const {data: tech} = useLivePreview({initialData: rawTech.value, depth: 2})

if (!rawTech.value) {
  throw showError({
    statusCode: 404,
  })
}

const {data: list} = await useAsyncData('projects', () => projects.fetch())

const filteredProjects = computed(() => {
  if (!list.value) {
    return
  }

  return list.value.projects.filter(project => {
    return project.technologies?.some(tech => tech.slug === slug)
  })
})

useSeoMeta({
  title: tech.value?.name,
  description: tech.value?.description as unknown as string,
  ogUrl: `${baseUrl}/tech/${route.params.slug}`,
  ogTitle: tech.value?.title || tech.value?.name,
  ogDescription: tech.value?.description as unknown as string,
  ogImage: resolveMediaUrl(tech.value?.image),
  ogImageAlt: resolveMediaAlt(tech.value?.image),
})
</script>

<template>
  <Loader :pending="pending">
    <main>
      <Container class="screen-content relative grid grid-cols-4 flex-col px-2 py-32 text-center lg:gap-8 lg:px-0">
        <div class="col-span-full mb-8 flex items-start pl-6 md:pl-12 lg:col-span-1 lg:mb-0 lg:mt-3 lg:pl-16">
          <NuxtImg
            v-bind="resolveMediaProps(tech?.image)"
            :width="256"
            :height="256"
            fit="cover"
            class="aspect-square w-16 rounded-lg lg:mx-auto lg:w-32"
          />
        </div>

        <header
          id="header"
          class="col-span-4 flex flex-col items-start justify-end gap-y-2 px-6 text-left md:px-12 lg:col-span-3 lg:pl-0 lg:pr-16"
        >
          <Heading as="h1" size="large" class="mx-auto w-full max-w-2xl md:mx-0">
            {{ tech?.title }}
          </Heading>
        </header>

        <aside
          class="col-span-full flex w-full flex-col rounded-lg p-6 md:px-12 lg:col-span-1 lg:pl-16 lg:pr-0"
        ></aside>

        <article class="col-span-4 lg:col-span-3">
          <main class="w-full px-6 pb-16 text-left md:px-12 lg:pl-0 lg:pr-16">
            <RichText :raw="tech?.description" />
          </main>
        </article>
      </Container>

      <section>
        <Container class="flex flex-col items-center">
          <Heading as="h1" size="big" class="mx-auto mb-8 w-full max-w-2xl text-center md:mx-0 md:mb-16">
            {{ $t('tech.projects') }} {{ ' ' }}{{ tech?.name }}
          </Heading>
          <div class="grid-col-1 mx-auto grid max-w-screen-xl gap-4 space-x-2 px-16 lg:grid-cols-3 xl:px-0">
            <ProjectRowPreview
              v-for="project of filteredProjects"
              :key="project.slug"
              :project="project"
              :industry="project.industriesSlugs"
            />
          </div>
        </Container>
      </section>

      <LazyCta :href="lp('/contact')" :label="$t('home.contact.ctaLabel')" />
    </main>
  </Loader>
</template>
